import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../utils/supabaseClient";
import "../styles/Home.css";
import "../styles/PartnersSection.css";
import "../styles/ServicesSection.css";
import "../styles/Footer.css";
import "../styles/PartnerSection.css";
import Industries from "../components/Industries";
import WebinarDisplay from "../components/WebinarDisplay";

const Home = () => {
  const partnersRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const [isHovered, setIsHovered] = useState(false);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const { data, error } = await supabase
          .from("partners")
          .select("name, icon_url, url")
          .order("name");

        if (error) {
          throw error;
        }

        setPartners(data);
      } catch (err) {
        console.error("Error fetching partners:", err);
        setError("Failed to load partners");
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  useEffect(() => {
    const trackPageVisit = async () => {
      try {
        const { error } = await supabase
          .from("page_visits")
          .insert([{ page_name: "home" }]);

        if (error) {
          console.error("Error tracking page visit:", error);
        }
      } catch (err) {
        console.error("Error tracking page visit:", err);
      }
    };

    trackPageVisit();
  }, []);

  useEffect(() => {
    const scrollContainer = partnersRef.current;
    if (!scrollContainer) return;

    let animationFrameId;
    let lastTimestamp = 0;
    const scrollSpeed = 0.3;

    const animate = (timestamp) => {
      animationFrameId = requestAnimationFrame(animate);

      if (!lastTimestamp) lastTimestamp = timestamp;
      const deltaTime = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      if (isHovered) {
        return;
      }

      const trackWidth = scrollContainer.scrollWidth;
      const containerWidth = scrollContainer.clientWidth;
      const cardElement = scrollContainer.querySelector(".partner-item");
      const cardWidth = cardElement
        ? cardElement.getBoundingClientRect().width
        : 0;
      const maxScrollLeft = Math.max(trackWidth - containerWidth, 0);

      if (maxScrollLeft <= cardWidth / 2) {
        scrollContainer.scrollLeft = 0;
        scrollPositionRef.current = 0;
        return;
      }

      const newPosition = scrollPositionRef.current + scrollSpeed * deltaTime;

      if (newPosition >= maxScrollLeft) {
        scrollPositionRef.current = 0;
        scrollContainer.scrollLeft = 0;
      } else {
        scrollPositionRef.current = newPosition;
        scrollContainer.scrollLeft = newPosition;
      }
    };

    scrollPositionRef.current = scrollContainer.scrollLeft;
    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isHovered, partners]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div className="home">
      {/* <Navbar /> */}
      <section className="hero">
        <video className="hero-video" autoPlay muted loop playsInline>
          <source src="/videos/hero-video2.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="hero-content">
          <h1>Innovating for Tomorrow</h1>
          <p>Empowering business with cutting-edge technology solutions</p>
          <button
            className="cta-button"
            onClick={() => (window.location.href = "/products")}
          >
            Explore products
          </button>
        </div>
      </section>

      <section className="partners-showcase">
        <div className="partners-showcase-container">
          <h2>Our Partners</h2>
          {loading ? (
            <div className="loading">Loading partners...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <div
              className="partners-scroll-container"
              ref={partnersRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="partners-scroll-track">
                {partners.map((partner) => (
                  <div key={partner.name} className="partner-item">
                    <a
                      href={partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={partner.icon_url}
                        alt={`${partner.name} logo`}
                      />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="webinar-section">
        <div className="webinar-section-container">
          <h2>Upcoming Events</h2>
          <WebinarDisplay />
        </div>
      </section>

      <Industries />
    </div>
  );
};

export default Home;
